import {
  OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_ENUM,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_OPTIONS,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_TITLES_ENUM,
  OPPORTUNITIES_AGGREGATE_FIELDS_ENUM,
  OPPORTUNITIES_AGGREGATE_GRAPH_BY_OPTIONS,
  OPPORTUNITIES_STACK_BY_OPTIONS,
  OPPORTUNITIES_STACK_BY_TYPE_ENUM,
  VALUATIONS_AGGREGATE_BY_TITLE,
} from '../../constants';
import React, { useEffect, useMemo, useState } from 'react';

import { MaGraph } from '../../../../../../components/Graphs/MaGraph';
import { getGraphLayout } from '../../../../../../components/Graphs/MaGraph/helpers';
import { titleize } from '../../../../../../utils/helpers';
import { useGetChartData } from './hooks/useGetChartData';

const SESSION_STORAGE_KEY = 'MaOpportunitiesValuationsChart';
const getInitialChartSettings = () => {
  const sessionSettings = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionSettings) {
    return JSON.parse(sessionSettings);
  }

  return {
    aggregateBy: OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COUNTRY,
    compareTo: OPPORTUNITIES_AGGREGATE_COMPARE_TO_ENUM.RESERVES,
    stackBy: OPPORTUNITIES_STACK_BY_TYPE_ENUM.NONE, // default value must be non-null otherwise options wont render
  };
};

export const ValuationsChart = () => {
  const {
    aggregateBy: defaultAggregateBy,
    compareTo: defaultCompareTo,
    stackBy: defaultStackBy,
  } = getInitialChartSettings();

  const [aggregateBy, setAggregateBy] = useState(defaultAggregateBy);
  const [compareTo, setCompareTo] = useState(defaultCompareTo);
  const [stackBy, setStackBy] = useState(defaultStackBy);
  const fields = useMemo(
    () => [
      OPPORTUNITIES_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM[compareTo],
      OPPORTUNITIES_AGGREGATE_FIELDS_ENUM.VALUATIONS,
    ],
    [compareTo],
  );

  useEffect(() => {
    // Save the current state to sessionStorage
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ aggregateBy, compareTo, stackBy }),
    );
  }, [aggregateBy, compareTo, stackBy]);

  // Automatically set stackBy to 'none' when aggregating by 'seller' to avoid redundancy
  useEffect(() => {
    if (aggregateBy === OPPORTUNITIES_AGGREGATE_BY_TYPE_ENUM.COMPANY) {
      setStackBy(OPPORTUNITIES_STACK_BY_TYPE_ENUM.NONE);
    }
  }, [aggregateBy]);

  const xConfig = {
    value: aggregateBy,
    setValue: setAggregateBy,
    options: OPPORTUNITIES_AGGREGATE_GRAPH_BY_OPTIONS,
  };
  const yConfig = {
    value: compareTo,
    setValue: setCompareTo,
    options: OPPORTUNITIES_AGGREGATE_COMPARE_TO_OPTIONS,
  };
  const stackByConfig = {
    value: stackBy || OPPORTUNITIES_STACK_BY_TYPE_ENUM.NONE,
    setValue: setStackBy,
    options: OPPORTUNITIES_STACK_BY_OPTIONS,
  };

  const yAxisTitle = VALUATIONS_AGGREGATE_BY_TITLE;
  const altYAxisTitle = OPPORTUNITIES_AGGREGATE_COMPARE_TO_TITLES_ENUM[compareTo];
  const graphLayout = getGraphLayout({
    showLegend: true,
    yAxisTitle: yAxisTitle,
    altYAxisTitle: altYAxisTitle,
  });

  const { data, loading, error } = useGetChartData({
    aggregateBy,
    compareTo,
    fields,
    stackBy,
  });

  if (error) return null;

  return (
    <MaGraph
      data={data}
      title={`NPV10 Valuation by ${titleize(aggregateBy)}`}
      loading={loading}
      xConfig={xConfig}
      yConfig={yConfig}
      stackByConfig={stackByConfig}
      graphLayout={graphLayout}
    />
  );
};
